import { SlideOver } from "@/components/application/slideout-menus/slide-over";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Badge } from "@/components/base/badges/badges";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { StatCard } from "@/components/application/metrics/stat-card";
import { useAdminSyncRunDetail } from "../../hooks/useAdminDocuments";

/**
 * UC-001 / princip #4 — slide-over s detailem konkrétního sync_run.
 *
 * Otevírá se z `SyncHistoryTable` po kliknutí na řádek. Zobrazí:
 *   - stat row (stav, doba, počet dokumentů, počet transakcí)
 *   - error_payload pretty-print (pokud non-null)
 *   - hint banner pokud state ∈ {selhal, castecny} ale error_payload je null
 *     (např. `csv_not_available` v empty scraper mode)
 *   - inline mini-table dokumentů stažených v rámci tohoto běhu
 */

export interface SyncRunDetailSlideOverProps {
  runId: string | null;
  onClose: () => void;
}

const STATE_LABEL: Record<string, string> = {
  kompletni: "Kompletní",
  castecny: "Částečný",
  selhal: "Selhal",
  running: "Probíhá",
};

const STATE_COLOR: Record<string, "success" | "warning" | "error" | "blue" | "gray"> = {
  kompletni: "success",
  castecny: "warning",
  selhal: "error",
  running: "blue",
};

const DOC_TYPE_LABEL: Record<string, string> = {
  invoice: "Faktura",
  invoice_statement: "Vyúčtování",
  transaction_statement: "Výpis transakcí",
  card_overview: "Přehled karet",
};

const REASON_LABEL: Record<string, string> = {
  fa_download_failed: "Stažení faktury selhalo (HTTP chyba / null)",
  fa_invalid_pdf: "Stažený soubor není validní PDF",
  fa_dates_not_found: "V PDF chybí DUZP i Datum vystavení",
  fa_duzp_not_found: "V PDF chybí DUZP (legacy reason kód)",
  fa_storage_put_failed: "Uložení faktury do storage selhalo",
  vy_storage_put_failed: "Uložení vyúčtování do storage selhalo",
  extracted_vy_storage_put_failed: "Uložení extrahovaného vyúčtování selhalo",
  pdfbox_split_failed_or_no_pages: "Extrakce stran z faktury selhala",
  csv_not_available: "CSV s transakcemi nedostupné z Axigonu",
  other: "Jiná chyba (viz aplikační log)",
};

type DownloadFailure = {
  axigon_doc_id: string;
  period_from?: string | null;
  period_to?: string | null;
  reason: string;
};

type StructuredErrorPayload = {
  download_failures?: DownloadFailure[];
  ingest_errors?: string[];
  downloaded_count?: number;
  download_failed_count?: number;
};

function isStructuredPayload(p: unknown): p is StructuredErrorPayload {
  if (p == null || typeof p !== "object") return false;
  const obj = p as Record<string, unknown>;
  return "download_failures" in obj || "ingest_errors" in obj;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} min ${s} s`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("cs-CZ");
  } catch {
    return iso;
  }
}

export function SyncRunDetailSlideOver({ runId, onClose }: SyncRunDetailSlideOverProps) {
  const query = useAdminSyncRunDetail(runId);
  const open = runId != null;
  const detail = query.data;

  return (
    <SlideOver
      open={open}
      title={detail ? `Detail synchronizace — ${detail.zone_code}` : "Detail synchronizace"}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 p-5 overflow-y-auto" data-testid="sync-run-detail">
        {query.isLoading && (
          <div className="flex items-center justify-center py-6">
            <LoadingIndicator label="Načítám detail…" />
          </div>
        )}

        {query.isError && (
          <InlineAlert variant="error">
            Detail synchronizace se nepodařilo načíst.
            {query.error instanceof Error ? `: ${query.error.message}` : null}
          </InlineAlert>
        )}

        {detail && (
          <>
            <header className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Badge color={STATE_COLOR[detail.state] ?? "gray"}>
                  {STATE_LABEL[detail.state] ?? detail.state}
                </Badge>
                <span className="text-sm text-tertiary">
                  Zahájeno {formatDate(detail.started_at)}
                </span>
              </div>
              <p className="text-xs text-tertiary m-0 font-mono break-all">
                run_id: {detail.sync_run_id}
              </p>
            </header>

            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Doba běhu" value={formatDuration(detail.duration_seconds)} />
              <StatCard
                label="Inventář"
                value={detail.inventory_count != null ? String(detail.inventory_count) : "—"}
              />
              <StatCard label="Dokumentů" value={String(detail.document_count)} />
              <StatCard label="Transakcí" value={String(detail.transaction_count)} />
            </div>

            {detail.error_payload != null && isStructuredPayload(detail.error_payload) ? (
              <section className="flex flex-col gap-3" data-testid="sync-error-structured">
                <h3 className="text-sm font-semibold text-primary m-0">Co se nepodařilo</h3>
                {detail.error_payload.download_failures &&
                  detail.error_payload.download_failures.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <h4 className="text-xs font-semibold text-tertiary m-0 uppercase tracking-wide">
                        Stahování dokumentů ({detail.error_payload.download_failures.length})
                      </h4>
                      <ul className="flex flex-col gap-2 m-0 p-0 list-none">
                        {detail.error_payload.download_failures.map((f, i) => (
                          <li
                            key={`${f.axigon_doc_id}-${i}`}
                            className="border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 rounded p-2 text-sm"
                          >
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-mono text-xs text-red-900 dark:text-red-200">
                                {f.axigon_doc_id}
                              </span>
                              <Badge color="error">{f.reason}</Badge>
                            </div>
                            <div className="text-xs text-red-800 dark:text-red-300 flex flex-wrap gap-x-3">
                              <span>{REASON_LABEL[f.reason] ?? f.reason}</span>
                              {f.period_from && f.period_to && (
                                <span className="text-tertiary">
                                  {f.period_from} – {f.period_to}
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                {detail.error_payload.ingest_errors &&
                  detail.error_payload.ingest_errors.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <h4 className="text-xs font-semibold text-tertiary m-0 uppercase tracking-wide">
                        Ingest transakcí ({detail.error_payload.ingest_errors.length})
                      </h4>
                      <ul className="flex flex-col gap-1 m-0 p-0 list-none">
                        {detail.error_payload.ingest_errors.map((e, i) => (
                          <li
                            key={`${e}-${i}`}
                            className="text-sm bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded px-2 py-1 flex items-center gap-2"
                          >
                            <Badge color="error">{e}</Badge>
                            <span className="text-xs text-red-800 dark:text-red-300">
                              {REASON_LABEL[e] ?? e}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </section>
            ) : detail.error_payload != null ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-primary m-0">Chyba</h3>
                <pre
                  className="text-xs bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200 rounded p-3 overflow-x-auto"
                  data-testid="sync-error-payload"
                >
                  {JSON.stringify(detail.error_payload, null, 2)}
                </pre>
              </section>
            ) : detail.state === "castecny" || detail.state === "selhal" ? (
              <InlineAlert variant="warning" data-testid="sync-no-error-hint">
                Sync skončil ve stavu „{STATE_LABEL[detail.state] ?? detail.state}“ bez specifické
                chyby v <code>error_payload</code>. Typicky to znamená, že fáze stahování nebo
                CSV ingest neměla data (např. <code>csv_not_available</code> v módu „empty“).
              </InlineAlert>
            ) : null}

            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-primary m-0">
                Stažené dokumenty ({detail.documents.length})
              </h3>
              {detail.documents.length === 0 ? (
                <p className="text-sm text-tertiary m-0">Žádné dokumenty.</p>
              ) : (
                <ul className="flex flex-col gap-2 m-0 p-0 list-none">
                  {detail.documents.map((d) => (
                    <li
                      key={`${d.axigon_doc_id}-${d.doc_type}`}
                      className="border border-secondary rounded p-2 text-sm"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono text-xs">{d.axigon_doc_id}</span>
                        <Badge color="gray">
                          {DOC_TYPE_LABEL[d.doc_type] ?? d.doc_type}
                        </Badge>
                      </div>
                      <div className="text-xs text-tertiary flex flex-wrap gap-x-3 gap-y-1">
                        <span>{formatBytes(d.size_bytes)}</span>
                        {d.period_from && d.period_to && (
                          <span>
                            {d.period_from} – {d.period_to}
                          </span>
                        )}
                        <span>{d.file_format.toUpperCase()}</span>
                        <span>{d.storage_backend}</span>
                        <a
                          href={`/api/admin/documents/${d.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-secondary hover:underline"
                          aria-label={`Stáhnout ${d.axigon_doc_id}`}
                        >
                          ↓ PDF
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </SlideOver>
  );
}
